import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Headset, Plus, Edit } from "lucide-react";

const TICKET_STATUS = ["open", "in_progress", "pending_customer", "resolved", "closed"];
const CATEGORIES = ["Endorsement", "Name Correction", "Address Correction", "Vehicle Correction", "Nominee Change", "Policy Copy", "Duplicate Policy", "Payment Issue", "NCB Correction", "Cancellation", "Refund", "Claim Assistance", "Other"];

export default function Tickets() {
  const { hasPerm } = useAuth();
  const [status, setStatus] = useState("all");
  const [customers, setCustomers] = useState([]);
  const params = status !== "all" ? { status } : {};
  const { items, total, loading, page, setPage, refetch } = useList("/tickets", params);
  useEffect(() => { api.get("/customers", { params: { limit: 200 } }).then(({ data }) => setCustomers(data.items || [])).catch(() => {}); }, []);

  const create = async (v) => {
    const c = customers.find((x) => x.id === v.customer_id);
    await api.post("/tickets", { ...v, customer_name: c?.name });
    refetch();
  };
  const edit = async (t, v) => { await api.put(`/tickets/${t.id}`, v); refetch(); };

  const createFields = [
    { name: "customer_id", label: "Customer", type: "select", required: true, options: customers.map((x) => ({ value: x.id, label: `${x.name} (${x.customer_id})` })) },
    { name: "category", label: "Category", type: "select", required: true, options: CATEGORIES },
    { name: "priority", label: "Priority", type: "select", options: ["high", "medium", "low"].map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "subject", label: "Subject" },
    { name: "policy_number", label: "Policy Number" },
    { name: "description", label: "Description", type: "textarea", full: true },
  ];
  const editFields = [
    { name: "status", label: "Status", type: "select", required: true, options: TICKET_STATUS.map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "priority", label: "Priority", type: "select", options: ["high", "medium", "low"].map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "resolution", label: "Resolution", type: "textarea", full: true },
  ];
  const columns = [
    { key: "ticket_number", header: "Ticket No", render: (r) => <span className="font-mono text-xs">{r.ticket_number}</span> },
    { key: "customer_name", header: "Customer", render: (r) => <span className="font-semibold">{r.customer_name || "—"}</span> },
    { key: "category", header: "Category", render: (r) => <span className="text-sm">{r.category}</span> },
    { key: "subject", header: "Subject", render: (r) => <span className="text-sm text-muted-foreground">{r.subject || "—"}</span> },
    { key: "priority", header: "Priority", render: (r) => <StatusBadge value={r.priority === "high" ? "hot" : r.priority === "low" ? "cold" : "warm"} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "created_at", header: "Created", render: (r) => <span className="text-sm">{fmtDate(r.created_at)}</span> },
    ...(hasPerm("tickets:edit") ? [{ key: "actions", header: "", render: (r) => (
      <div onClick={(e) => e.stopPropagation()}>
        <FormDialog title={`Update Ticket — ${r.ticket_number}`} fields={editFields} initial={{ status: r.status, priority: r.priority }} onSubmit={(v) => edit(r, v)} submitLabel="Update"
          trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid="ticket-edit-btn"><Edit className="h-4 w-4" /></Button>} />
      </div>
    ) }] : []),
  ];
  return (
    <div>
      <PageHeader title="Service Tickets" subtitle={`${total} service requests`} icon={Headset}
        actions={hasPerm("tickets:add") && (
          <FormDialog title="New Service Ticket" fields={createFields} onSubmit={create} submitLabel="Create Ticket" testId="create-ticket-dialog"
            trigger={<Button data-testid="create-ticket-btn"><Plus className="h-4 w-4 mr-1.5" /> New Ticket</Button>} />
        )} />
      <div className="flex gap-3 mb-4">
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-52" data-testid="ticket-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{TICKET_STATUS.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
      </div>
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="ticket-table-row" emptyText="No service tickets" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
